import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, CheckCircle, Mic, Shield, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicSchedulePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(null);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/scheduling/info/${token}`);
        setData(res.data);
        if (res.data.status === 'scheduled') {
          setBooked(res.data.selected_slot);
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired scheduling link');
      } finally {
        setLoading(false);
      }
    };
    fetchInfo();
  }, [token]);

  const handleBook = async () => {
    if (!selectedSlot) return;
    setBooking(true);
    try {
      const res = await axios.post(`${API_URL}/api/scheduling/book/${token}`, { slot_id: selectedSlot.slot_id });
      setBooked(selectedSlot);
      setData(prev => ({ ...prev, status: 'scheduled' }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Booking failed');
    } finally {
      setBooking(false);
    }
  };

  // Group slots by date
  const groupedSlots = {};
  if (data?.available_slots) {
    data.available_slots.forEach(slot => {
      const date = slot.display_date;
      if (!groupedSlots[date]) groupedSlots[date] = [];
      groupedSlots[date].push(slot);
    });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#7C3AED] animate-spin" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-[#E11D48]/15 flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-[#E11D48]" />
          </div>
          <h1 className="font-outfit text-xl font-semibold text-white mb-2">Link Not Found</h1>
          <p className="text-[#52525B] text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4" data-testid="public-schedule-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-outfit text-3xl font-bold text-white mb-1">Vocala<span className="text-[#7C3AED]">.</span></h1>
          <p className="text-[#52525B] text-sm">AI Technical Screening Platform</p>
        </div>

        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl overflow-hidden">
          {/* Candidate info */}
          <div className="px-6 py-5 border-b border-[#18181B]">
            <p className="text-[#A1A1AA] text-sm">Hello, <span className="text-white font-medium">{data.candidate_name}</span></p>
            <h2 className="font-outfit text-lg font-semibold text-white mt-1">
              Schedule your screening for <span className="text-[#7C3AED]">{data.job_title}</span>
            </h2>
          </div>

          {booked ? (
            /* Confirmation view */
            <div className="p-8 text-center" data-testid="booking-confirmed">
              <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }} className="w-16 h-16 rounded-full bg-[#10B981]/15 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-[#10B981]" />
              </motion.div>
              <h3 className="font-outfit text-xl font-semibold text-white mb-2">You're All Set!</h3>
              <p className="text-[#A1A1AA] text-sm mb-6">Your AI screening has been scheduled.</p>
              <div className="bg-[#121212] border border-[#7C3AED]/30 rounded-xl p-5 inline-block">
                <p className="text-[#52525B] text-xs uppercase tracking-wider mb-1">Scheduled For</p>
                <p className="text-white font-semibold text-lg">{booked.display_date}</p>
                <p className="text-[#7C3AED] font-mono text-sm mt-1">{booked.display_time} ({booked.duration_minutes} min)</p>
              </div>
              <div className="mt-8 grid grid-cols-3 gap-4 text-center max-w-md mx-auto">
                <div className="p-3 bg-[#121212] rounded-lg border border-[#18181B]">
                  <Mic className="w-4 h-4 text-[#7C3AED] mx-auto mb-1" />
                  <p className="text-[10px] text-[#52525B]">AI Interview</p>
                </div>
                <div className="p-3 bg-[#121212] rounded-lg border border-[#18181B]">
                  <Clock className="w-4 h-4 text-[#06B6D4] mx-auto mb-1" />
                  <p className="text-[10px] text-[#52525B]">~30 Minutes</p>
                </div>
                <div className="p-3 bg-[#121212] rounded-lg border border-[#18181B]">
                  <Shield className="w-4 h-4 text-[#10B981] mx-auto mb-1" />
                  <p className="text-[10px] text-[#52525B]">Proctored</p>
                </div>
              </div>
              <p className="text-[#52525B] text-xs mt-6">A confirmation email has been sent. You'll receive a link to join before your session.</p>
            </div>
          ) : (
            /* Slot selection */
            <div className="p-6">
              <p className="text-[#A1A1AA] text-sm mb-5">Select a time slot that works best for you:</p>
              <div className="space-y-5" data-testid="slot-selection">
                {Object.entries(groupedSlots).map(([date, slots]) => (
                  <div key={date}>
                    <p className="text-xs text-[#52525B] font-medium uppercase tracking-wider mb-2">{date}</p>
                    <div className="grid grid-cols-3 gap-2">
                      {slots.map(slot => (
                        <button
                          key={slot.slot_id}
                          data-testid={`slot-${slot.slot_id}`}
                          onClick={() => setSelectedSlot(slot)}
                          className={`px-4 py-3 rounded-lg border text-sm font-medium transition-all duration-200 ${
                            selectedSlot?.slot_id === slot.slot_id
                              ? 'bg-[#7C3AED]/20 border-[#7C3AED] text-[#7C3AED] shadow-[0_0_20px_rgba(124,58,237,0.15)]'
                              : 'bg-[#121212] border-[#27272A] text-[#A1A1AA] hover:border-[#3F3F46] hover:text-white'
                          }`}
                        >
                          <Clock className="w-3.5 h-3.5 mx-auto mb-1" />
                          {slot.display_time}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {error && <p className="text-[#E11D48] text-sm mt-4">{error}</p>}

              <button
                data-testid="confirm-booking-btn"
                onClick={handleBook}
                disabled={!selectedSlot || booking}
                className="w-full mt-6 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-medium py-3 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {booking ? 'Scheduling...' : selectedSlot ? `Confirm ${selectedSlot.display_time} on ${selectedSlot.display_date}` : 'Select a time slot'}
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-[#27272A] text-xs mt-6">Powered by Vocala AI Screening</p>
      </motion.div>
    </div>
  );
}
